import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Alert } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ProfileHelper from 'utils/apiHelpers/profilehelper.ts';
import { updateProfile as updateProfileAction } from 'redux/reducers/profiles';
import { wsSendUpdatedProfile } from 'redux/middlewares/signalr';
import { profileTypesSelector } from 'redux/reducers/profileTypes';
import { isEqual } from 'lodash';
import ProfileInputs from '../NewProfileForm/ProfileInputs/ProfileInputs';

const hasFormValuesChanged = (keys, currentValues, newValues) =>
  keys.some((key) => !isEqual(currentValues[key], newValues[key]));

const UpdateProfileForm = ({ visible, profile, onFormCompleted, onCancel }) => {
  const [formErrors, setFormErrors] = React.useState({});
  const [formLoading, setFormLoading] = React.useState(false);
  const [updateButtonDisabled, setUpdateButtonDisabled] = React.useState(true);
  const [showError409, setShowError409] = React.useState(false);
  const dispatch = useDispatch();
  const profileTypeList = useSelector(profileTypesSelector);
  const { t } = useTranslation();

  const profileType =
    profile && profileTypeList.find((pt) => pt.profileTypeId === profile.profileTypeId);

  const formRef = createRef();

  const resetForm = () => {
    setFormErrors({});
    setFormLoading(false);
    setUpdateButtonDisabled(true);
    setShowError409(false);
  };

  React.useEffect(() => {
    resetForm();
  }, [profile, visible]);

  const saveValidFormData = async () =>
    new Promise((resolve) => {
      formRef.current
        .validateFields()
        .then((values) => {
          resolve({ values });
        })
        .catch((error) => {
          resolve({ error });
        });
    });

  const updateProfile = async (options, profileId) => {
    const result = await ProfileHelper.update(options, profileId);
    dispatch(updateProfileAction(result.data));
    dispatch(wsSendUpdatedProfile(result.data));
  };

  const onUpdateClick = async () => {
    setShowError409(false);
    setFormLoading(true);

    const { values } = await saveValidFormData();
    const validFormValues = values;
    const updateOptions = {
      profileTypeId: profile.profileTypeId,
      formValues: { ...profile.formValues, ...values },
    };
    if (validFormValues) {
      try {
        await updateProfile(updateOptions, profile.profileId);
        onFormCompleted();
        resetForm();
      } catch (error) {
        if (error.response) {
          if (error.response.status === 400) {
            setFormErrors(error.response.data);
          } else if (error.response.status === 409) {
            setShowError409(true);
          }
        }
      }
    }
    setFormLoading(false);
  };

  const checkForNewValues = () => {
    const currentValues = profile.formValues;
    const newValues = formRef.current.getFieldsValue();
    setUpdateButtonDisabled(
      !hasFormValuesChanged(Object.keys(profileType.editableFields), currentValues, newValues),
    );
  };

  const resetFormError = (inputId) => {
    if (formErrors[inputId]) {
      setFormErrors((errors) => ({ ...errors, [inputId]: '' }));
    }
  };

  const handleInputChange = (inputId) => {
    checkForNewValues();
    resetFormError(inputId);
  };

  const handle409AlertClose = () => {
    setShowError409(false);
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      width={780}
      title={profile && t('updateProfileForm.title', { provider: profile.profileName })}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('forms.cancel')}
        </Button>,
        <Button
          key="ok"
          type="primary"
          onClick={onUpdateClick}
          loading={formLoading}
          disabled={updateButtonDisabled}
        >
          {t('forms.update')}
        </Button>,
      ]}
      destroyOnClose
    >
      {profile && (
        <ProfileInputs
          values={profile.formValues}
          editableFields={profileType.editableFields}
          form={formRef}
          onChange={handleInputChange}
          onSubmit={updateButtonDisabled ? undefined : onUpdateClick}
          errors={formErrors}
        />
      )}
      {showError409 && (
        <Alert
          message={t('errors.errorNumber', { number: 409 })}
          description={t('errors.409message')}
          type="error"
          showIcon
          closable
          afterClose={handle409AlertClose}
        />
      )}
    </Modal>
  );
};

UpdateProfileForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  profile: PropTypes.shape({
    profileId: PropTypes.number,
    profileName: PropTypes.string,
    profileTypeId: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    formValues: PropTypes.object,
  }),
  onFormCompleted: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

UpdateProfileForm.defaultProps = {
  profile: undefined,
};

export default UpdateProfileForm;
